import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import React from 'react'
import { motion } from 'framer-motion'
import {
  CreditCardIcon,
  CalendarIcon,
  ArrowTrendingUpIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline'
import {
  formatCurrency,
  formatDate as formatLocaleDate,
} from '../../../../shared/lib/format'

export interface SubscriptionData {
  currentPlan: {
    type: string
    name: string
    description: string
    color: string
    badge?: string
    price: {
      monthly: number
      yearly: number
    }
    expires?: string
    daysUntilExpiration?: number
    isExpiringSoon: boolean
    isExpired: boolean
  }
  usage: {
    links: {
      used: number
      limit: number
      percentage: number
      unlimited: boolean
    }
    clicks: {
      used: number
      limit: number
      percentage: number
      unlimited: boolean
    }
  }
  features: Record<string, any>
  nextBilling: {
    date?: string
    amount: number
    currency: string
  }
}

export interface UsageData {
  currentMonth: {
    linksCreated: number
    totalClicks: number
    year: number
    month: number
  }
  limits: {
    links: number
    clicks: number
  }
  plan: string
  percentageUsed: {
    links: number
    clicks: number
  }
  isWithinLimits: {
    links: boolean
    clicks: boolean
  }
}

interface SubscriptionInfoProps {
  data: SubscriptionData
  usageData?: UsageData
  onUpgrade?: () => void
  onManage?: () => void
}

const SubscriptionInfo: React.FC<SubscriptionInfoProps> = ({
  data,
  usageData,
  onUpgrade,
  onManage,
}) => {
  const { t } = useTranslation()
  const { currentPlan, usage, nextBilling } = data

  // Use usageData if available, otherwise fall back to data.usage
  const actualUsage = usageData
    ? {
        links: {
          used: usageData.currentMonth.linksCreated,
          limit: usageData.limits.links,
          percentage: usageData.percentageUsed.links,
          unlimited: usageData.limits.links === -1,
        },
        clicks: {
          used: usageData.currentMonth.totalClicks,
          limit: usageData.limits.clicks,
          percentage: usageData.percentageUsed.clicks,
          unlimited: usageData.limits.clicks === -1,
        },
      }
    : usage

  // Format date
  const formatDate = (dateString?: string) => {
    if (!dateString) {
      return 'No expiration'
    }
    return formatLocaleDate(dateString)
  }

  // Get status color and icon
  const getStatusDisplay = () => {
    if (currentPlan.isExpired) {
      return {
        color: 'red',
        icon: XCircleIcon,
        text: t('subscription.expired'),
        bgClass: 'bg-red-50 dark:bg-red-900/20',
        textClass: 'text-red-700 dark:text-red-300',
        iconClass: 'text-red-500',
      }
    }

    if (currentPlan.isExpiringSoon) {
      return {
        color: 'yellow',
        icon: ExclamationTriangleIcon,
        text: t('subscription.expiringSoon', {
          days: String(currentPlan.daysUntilExpiration ?? 0),
        }),
        bgClass: 'bg-yellow-50 dark:bg-yellow-900/20',
        textClass: 'text-yellow-700 dark:text-yellow-300',
        iconClass: 'text-yellow-500',
      }
    }

    return {
      color: 'green',
      icon: CheckCircleIcon,
      text: t('subscription.active'),
      bgClass: 'bg-green-50 dark:bg-green-900/20',
      textClass: 'text-green-700 dark:text-green-300',
      iconClass: 'text-green-500',
    }
  }

  const status = getStatusDisplay()

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      {/* Header */}
      <div className="border-b border-gray-200 p-6 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CreditCardIcon className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t('subscription.title')}
            </h2>
          </div>

          {/* Status Badge */}
          <div
            className={`flex items-center gap-2 rounded-full px-3 py-1 ${status.bgClass}`}
          >
            <status.icon className={`h-4 w-4 ${status.iconClass}`} />
            <span className={`text-sm font-medium ${status.textClass}`}>
              {status.text}
            </span>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* 좁은 칸(대시보드 오른쪽 열)에 들어간다. 화면 너비 기준으로 2열을
            강제하면 글자가 한 자씩 끊겨 내려간다. 세로로 쌓는다. */}
        <div className="flex flex-col gap-6">
          {/* Current Plan */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('subscription.currentPlan')}
            </h3>

            <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-700">
              <div className="mb-3 flex items-center justify-between">
                <div>
                  {/* 배지가 제목과 한 줄에 들어가도록 묶는다. 좁은 칸에서
                      글자 중간에 끊기면 '가장 / 인기' 처럼 읽힌다 */}
                  <h4 className="flex flex-wrap items-center gap-2 text-lg font-bold text-gray-900 dark:text-white">
                    <span>{currentPlan.name}</span>
                    {currentPlan.badge && (
                      <span className="whitespace-nowrap rounded-full bg-primary-100 px-2 py-0.5 text-xs font-semibold text-primary-800 dark:bg-primary-900 dark:text-primary-200">
                        {currentPlan.badge}
                      </span>
                    )}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {currentPlan.description}
                  </p>
                </div>

                <div className="text-right">
                  {/* 통화·천 단위 구분은 화면 언어 규칙을 따른다.
                      직접 ₩ 를 붙이고 숫자를 그대로 쓰면 10000 처럼 나온다 */}
                  <div className="whitespace-nowrap text-2xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(currentPlan.price.monthly, 'KRW')}
                    <span className="text-sm font-normal text-gray-600 dark:text-gray-400">
                      /{t('pricing.perMonth')}
                    </span>
                  </div>
                  {currentPlan.price.yearly > 0 && (
                    <div className="whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {formatCurrency(currentPlan.price.yearly, 'KRW')}/
                      {t('pricing.perYear')}
                    </div>
                  )}
                </div>
              </div>

              {/* Expiration Info */}
              {currentPlan.expires && (
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <CalendarIcon className="h-4 w-4" />
                  <span>
                    {currentPlan.type === 'free'
                      ? 'No expiration'
                      : `Expires: ${formatDate(currentPlan.expires)}`}
                  </span>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              {currentPlan.type !== 'enterprise' && onUpgrade && (
                <button
                  onClick={onUpgrade}
                  className="flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-700"
                >
                  <ArrowTrendingUpIcon className="h-4 w-4" />
                  {t('subscription.upgrade')}
                </button>
              )}

              {currentPlan.type !== 'free' && onManage && (
                <button
                  onClick={onManage}
                  className="flex-1 whitespace-nowrap rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-900 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-white dark:hover:bg-gray-700"
                >
                  {t('subscription.manage')}
                </button>
              )}
            </div>
          </div>

          {/* Usage Statistics */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('subscription.monthlyUsage')}
            </h3>

            {/* Links Usage */}
            <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-700">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('subscription.shortLinks')}
                </span>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {actualUsage.links.unlimited
                    ? t('subscription.used', {
                        count: actualUsage.links.used.toLocaleString(),
                      })
                    : `${actualUsage.links.used.toLocaleString()} / ${actualUsage.links.limit.toLocaleString()}`}
                </span>
              </div>

              {!actualUsage.links.unlimited && (
                <>
                  <div className="mb-2 h-2 w-full rounded-full bg-gray-200 dark:bg-gray-600">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{
                        width: `${Math.min(actualUsage.links.percentage, 100)}%`,
                      }}
                      transition={{ duration: 1, ease: 'easeOut' }}
                      className={`h-2 rounded-full ${
                        actualUsage.links.percentage >= 90
                          ? 'bg-red-500'
                          : actualUsage.links.percentage >= 75
                            ? 'bg-yellow-500'
                            : 'bg-blue-500'
                      }`}
                    />
                  </div>
                  <div
                    className={`text-xs ${
                      actualUsage.links.percentage >= 90
                        ? 'text-red-600 dark:text-red-400'
                        : actualUsage.links.percentage >= 75
                          ? 'text-yellow-600 dark:text-yellow-400'
                          : 'text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    {t('subscription.percentUsed', {
                      percent: actualUsage.links.percentage.toFixed(1),
                    })}
                  </div>
                </>
              )}
            </div>

            {/* Clicks Usage */}
            <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-700">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('subscription.totalClicks')}
                </span>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {actualUsage.clicks.unlimited
                    ? t('subscription.usedClicks', {
                        count: actualUsage.clicks.used.toLocaleString(),
                      })
                    : `${actualUsage.clicks.used.toLocaleString()} / ${actualUsage.clicks.limit.toLocaleString()}`}
                </span>
              </div>

              {!actualUsage.clicks.unlimited && (
                <>
                  <div className="mb-2 h-2 w-full rounded-full bg-gray-200 dark:bg-gray-600">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{
                        width: `${Math.min(actualUsage.clicks.percentage, 100)}%`,
                      }}
                      transition={{ duration: 1, ease: 'easeOut', delay: 0.2 }}
                      className={`h-2 rounded-full ${
                        actualUsage.clicks.percentage >= 90
                          ? 'bg-red-500'
                          : actualUsage.clicks.percentage >= 75
                            ? 'bg-yellow-500'
                            : 'bg-green-500'
                      }`}
                    />
                  </div>
                  <div
                    className={`text-xs ${
                      actualUsage.clicks.percentage >= 90
                        ? 'text-red-600 dark:text-red-400'
                        : actualUsage.clicks.percentage >= 75
                          ? 'text-yellow-600 dark:text-yellow-400'
                          : 'text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    {t('subscription.percentUsed', {
                      percent: actualUsage.clicks.percentage.toFixed(1),
                    })}
                  </div>
                </>
              )}
            </div>

            {/* Next Billing */}
            {nextBilling.date && currentPlan.type !== 'free' && (
              <div className="rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20">
                <h4 className="mb-2 text-sm font-medium text-blue-900 dark:text-blue-300">
                  Next Billing
                </h4>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-blue-700 dark:text-blue-300">
                    {formatDate(nextBilling.date)}
                  </span>
                  <span className="font-semibold text-blue-900 dark:text-blue-200">
                    {formatCurrency(
                      nextBilling.amount,
                      nextBilling.currency || 'KRW'
                    )}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

/** 모달 상태가 바뀌어도 값이 같으면 다시 그리지 않는다 */
export default memo(SubscriptionInfo)
